/**
 * Ring size request and direct checkout for the product form.
 *
 * Markup comes from snippets/ring-size-request.liquid. This script:
 *   1. Validates the size request for resizeable rings (trimmed, required,
 *      length limit) and shows an inline error. Nothing is added or redirected
 *      while the entry is invalid.
 *   2. Add to Cart: after the same check, the theme's own handler adds the ring
 *      and shows its usual message. This script steps aside.
 *      Buy it now: after the same check, adds the selected variant, quantity and
 *      line item properties to the CURRENT basket with /cart/add.js, waits for
 *      the confirmed response, then opens checkout. It never clears, rebuilds or
 *      replaces the basket. It is our own button, not Shopify's accelerated one.
 *   3. On any failure it stays on the page, keeps what the customer typed,
 *      and explains what went wrong.
 *
 * The listener sits on the form in the capture phase, so it runs before the
 * theme's own add to cart handler and can stop it (for an invalid size). The
 * theme code is not edited.
 */
(function () {
  'use strict';

  var LOADING_CLASS = 'product-details__add-to-cart-button--loading';
  var instances = [];

  function RingSizeRequest(root) {
    this.root = root;
    this.form = root.closest('form');
    if (!this.form) return;

    this.button = this.form.querySelector('[data-product-atc]'); // Add to Cart (theme's)
    this.buyNow = this.form.querySelector('[data-ring-buy-now]'); // Buy it now (ours), if shown
    this.input = root.querySelector('[data-ring-size-input]');
    this.errorEl = root.querySelector('[data-ring-size-error]');
    this.noResize = root.querySelector('[data-ring-size-no-resize]');
    this.noResizeValue = root.getAttribute('data-no-resize-value') || 'No resize';
    this.mode = root.getAttribute('data-mode');
    this.direct = !!this.buyNow;
    this.required = root.getAttribute('data-required') !== 'false';
    this.maxLength = parseInt(root.getAttribute('data-max-length'), 10) || 30;
    this.checkoutUrl = root.getAttribute('data-checkout-url') || '/checkout';
    this.cartUrl = root.getAttribute('data-cart-url') || '/cart';
    this.addUrl = (root.getAttribute('data-cart-add-url') || '/cart/add') + '.js';
    this.busy = false;

    // No size to check and no Buy it now button: leave the theme alone.
    if (this.mode !== 'resizeable' && !this.buyNow) return;

    this.buildLiveRegions();

    var self = this;
    this.onClick = function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var buy = target.closest('[data-ring-buy-now]');
      if (buy && self.form.contains(buy)) return self.onBuyNow(event);
      var atc = target.closest('[data-product-atc]');
      if (atc && self.form.contains(atc)) self.onAddToCart(event);
    };
    // Enter in the size box submits the form, which is Add to Cart.
    this.onSubmit = function (event) {
      self.onAddToCart(event);
    };
    this.form.addEventListener('click', this.onClick, true);
    this.form.addEventListener('submit', this.onSubmit, true);

    if (this.input) {
      this.input.addEventListener('input', function () {
        self.clearError();
      });
    }

    // The theme enables and disables Add to Cart as variants change (sold out,
    // unavailable). Buy it now follows it.
    if (this.buyNow && this.button && window.MutationObserver) {
      var mirror = function () {
        self.buyNow.disabled = self.button.disabled;
      };
      new MutationObserver(mirror).observe(this.button, { attributes: true, attributeFilter: ['disabled'] });
      mirror();
    }

    // "No resize" tick box. Ticking writes the answer into the field itself and
    // locks it, so the field is the single source of truth: every submit path
    // (this script, the theme's own handler, a native submit) sends the same value.
    if (this.noResize && this.input) {
      this.noResize.addEventListener('change', function () {
        self.clearError();
        if (self.noResize.checked) {
          self.input.value = self.noResizeValue;
          self.input.readOnly = true;
        } else {
          self.input.value = '';
          self.input.readOnly = false;
        }
      });
      // A page restored from cache can come back with the box ticked.
      if (this.noResize.checked) {
        this.input.value = this.noResizeValue;
        this.input.readOnly = true;
      }
    }
  }

  RingSizeRequest.prototype.buildLiveRegions = function () {
    var area = this.form.querySelector('.product-details__add-to-cart-area') || this.form;

    // Polite status for the loading state, assertive alert for add failures.
    this.statusEl = document.createElement('span');
    this.statusEl.className = 'visually-hidden';
    this.statusEl.setAttribute('role', 'status');
    this.statusEl.setAttribute('aria-live', 'polite');

    this.alertEl = document.createElement('div');
    this.alertEl.className = 'ring-size-request__alert';
    this.alertEl.setAttribute('role', 'alert');
    this.alertEl.setAttribute('data-ring-size-alert', '');

    area.appendChild(this.statusEl);
    area.appendChild(this.alertEl);
  };

  function block(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  /** Add to Cart (or Enter in the size box): check the size, then let the theme add as normal. */
  RingSizeRequest.prototype.onAddToCart = function (event) {
    if (this.busy) return block(event); // a Buy it now request is already in flight
    if (this.button && this.button.disabled) return;
    if (this.mode === 'resizeable' && !this.validate()) block(event);
  };

  /** Buy it now: check the size, add to the current basket, then open checkout. */
  RingSizeRequest.prototype.onBuyNow = function (event) {
    block(event);
    if (this.busy) return;
    if (this.buyNow && this.buyNow.disabled) return;
    if (this.mode === 'resizeable' && !this.validate()) return;
    this.addAndCheckout();
  };

  RingSizeRequest.prototype.validate = function () {
    if (!this.input) return true;

    var value = this.input.value.trim();
    this.input.value = value;

    if (value === '' && this.required) {
      this.showError(this.root.getAttribute('data-error-empty'));
      return false;
    }
    if (value.length > this.maxLength) {
      var tooLong = this.root.getAttribute('data-error-too-long') || '';
      this.showError(tooLong.replace('{max}', String(this.maxLength)));
      return false;
    }
    this.clearError();
    return true;
  };

  RingSizeRequest.prototype.showError = function (message) {
    this.errorEl.textContent = message;
    this.root.classList.add('ring-size-request--has-error');
    this.input.setAttribute('aria-invalid', 'true');
    this.input.focus();
  };

  RingSizeRequest.prototype.clearError = function () {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.root.classList.remove('ring-size-request--has-error');
    this.input.removeAttribute('aria-invalid');
  };

  RingSizeRequest.prototype.setLoading = function (isLoading) {
    var button = this.buyNow;
    if (!button) return;
    button.classList.toggle(LOADING_CLASS, isLoading);
    if (isLoading) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
  };

  RingSizeRequest.prototype.showFailure = function (message, withCartLink) {
    this.setLoading(false);
    this.statusEl.textContent = '';
    this.alertEl.textContent = message + ' ';
    if (withCartLink) {
      var link = document.createElement('a');
      link.href = this.cartUrl;
      link.textContent = this.root.getAttribute('data-cart-link-text') || 'cart';
      this.alertEl.appendChild(link);
    }
    this.alertEl.classList.add('ring-size-request__alert--visible');
    this.busy = false;
  };

  RingSizeRequest.prototype.reset = function () {
    this.busy = false;
    this.setLoading(false);
    if (this.statusEl) this.statusEl.textContent = '';
  };

  RingSizeRequest.prototype.addAndCheckout = function () {
    if (this.busy) return; // ignore double clicks and repeat taps
    this.busy = true;

    this.alertEl.textContent = '';
    this.alertEl.classList.remove('ring-size-request__alert--visible');
    this.statusEl.textContent = 'Adding to your basket';
    this.setLoading(true);

    var self = this;
    var body = new FormData(this.form);
    // Only reachable when blank is allowed (tick box off, size not required):
    // send nothing rather than an empty property.
    if (this.input && this.input.value.trim() === '') body.delete(this.input.name);

    fetch(this.addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: body
    })
      .then(function (response) {
        return response.json().then(
          function (data) {
            return { ok: response.ok, data: data };
          },
          function () {
            return { ok: false, data: null };
          }
        );
      })
      .then(function (result) {
        var data = result.data;
        var confirmed = result.ok && data && !data.status && !data.errors && (data.key || data.id || data.items);

        if (!confirmed) {
          var detail = data && (data.description || data.message);
          var fallback = self.root.getAttribute('data-error-add');
          // Shopify's own wording, e.g. "All 1 are in your cart" or sold out.
          self.showFailure(typeof detail === 'string' && detail ? detail : fallback, false);
          return;
        }

        // Confirmed. Keep the loading state on while the browser navigates.
        window.location.assign(self.checkoutUrl);
      })
      .catch(function () {
        // The add may or may not have reached Shopify, so do not add again
        // blindly and do not redirect. Point the customer at their cart.
        self.showFailure(self.root.getAttribute('data-error-network'), true);
      });
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-ring-size-request]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.ringSizeRequest) return;
      root.ringSizeRequest = new RingSizeRequest(root);
      instances.push(root.ringSizeRequest);
    });
  }

  // Coming back to a cached page (browser back from checkout) must not leave a
  // stuck loading button.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    instances.forEach(function (instance) {
      if (instance.reset) instance.reset();
    });
  });

  // Theme editor reloads sections without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
})();
